import React, { useState } from 'react';
import Select from 'react-select';
import { useAuth } from '../../contexts/AuthContext';

// Placeholder: You can import specific forms for each entity type
import AddClientForm from '../forms/AddClientForm';
import AddSupplierForm from '../forms/AddSupplierForm';
import AddCategoryForm from '../forms/AddCategoryForm';
import AddDoctorForm from '../forms/AddDoctorForm';
import AddProductForm from '../forms/AddProductForm';

const ENTITY_LABELS = {
  client: 'Client',
  product: 'Produit',
  supplier: 'Fournisseur',
  doctor: 'Médecin',
  category: 'Catégorie',
  // Add more as needed
};

function EntitySelectWithAdd({
  entityType,
  options,
  value,
  onChange,
  fetchOptions,
  label,
  showAddButton = true,
  ...props
}) {
  const [isModalOpen, setIsModalOpen] = useState(false);
  const { canCreateData } = useAuth();

  // Handle modal form submission
  const handleAddEntity = async (newEntity) => {
    try {
      console.log('Entity added successfully:', newEntity);
      
      // Get the entity ID from the response - handle different response formats
      let entityId = null;
      if (newEntity) {
        // Handle different response formats from different routes
        entityId = newEntity.id || 
                   newEntity.clientId || 
                   newEntity.doctorId || 
                   newEntity.supplierId || 
                   newEntity.productId ||
                   newEntity.insertId;
      }
      
      console.log('New entity ID:', entityId);
      
      // Close the modal first
      setIsModalOpen(false);
      
      // Refresh the options list and wait for it to complete
      if (fetchOptions) {
        console.log('Refreshing options list...');
        await fetchOptions();
        console.log('Options list refreshed');
      }
      
      // Update the selected value after options are refreshed
      if (onChange && entityId) {
        console.log('Setting new entity ID:', entityId);
        // Add a small delay to ensure the options are updated
        setTimeout(() => {
          onChange(entityId.toString());
        }, 100);
      }
    } catch (error) {
      console.error('Error handling entity addition:', error);
    }
  };

  // Handle modal cancellation
  const handleCancel = () => {
    setIsModalOpen(false);
  };

  // Handle add button click with permission check
  const handleAddClick = () => {
    if (!canCreateData()) {
      alert('Vous n\'avez pas les permissions nécessaires pour créer un ' + ENTITY_LABELS[entityType] || entityType);
      return;
    }
    setIsModalOpen(true);
  };

  // Placeholder for rendering the correct form
  const renderAddForm = () => {
    if (entityType === 'client') {
      return <AddClientForm onSuccess={handleAddEntity} onCancel={handleCancel} />;
    }
    if (entityType === 'supplier') {
      return <AddSupplierForm onSuccess={handleAddEntity} onCancel={handleCancel} />;
    }
    if (entityType === 'category') {
      return <AddCategoryForm onSuccess={handleAddEntity} onCancel={handleCancel} />;
    }
    if (entityType === 'doctor') {
      return <AddDoctorForm onSuccess={handleAddEntity} onCancel={handleCancel} />;
    }
    if (entityType === 'product') {
      return <AddProductForm onSuccess={handleAddEntity} onCancel={handleCancel} />;
    }
    return (
      <div className="p-4">
        <p className="mb-2 text-gray-700">Add new {ENTITY_LABELS[entityType] || entityType}</p>
        <div className="bg-gray-100 p-4 rounded">[Add {ENTITY_LABELS[entityType] || entityType} Form Here]</div>
        <button
          className="mt-4 px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          onClick={() => handleAddEntity({})}
        >
          Save
        </button>
      </div>
    );
  };

  return (
    <div className="flex items-center space-x-2">
      <div className="flex-1">
        <label className="block text-sm font-medium text-gray-700 mb-1">{label || ENTITY_LABELS[entityType] || entityType}</label>
        <Select
          classNamePrefix="entity-select"
          options={options.map(opt => opt.value !== undefined && opt.label !== undefined ? opt : { value: opt.id, label: opt.name })}
          value={typeof value === 'object' && value !== null ? value : (options.find(opt => (opt.value || opt.id) === value) || null)}
          onChange={opt => onChange(opt ? (opt.value || opt.id) : '')}
          isSearchable
          isClearable
          menuPortalTarget={typeof window !== 'undefined' ? document.body : null}
          styles={{
            menu: base => ({ ...base, maxHeight: 300, overflowY: 'auto', scrollBehavior: 'smooth', zIndex: 9999 }),
            menuPortal: base => ({ ...base, zIndex: 9999 })
          }}
          placeholder={`Sélectionner ${ENTITY_LABELS[entityType] || entityType}...`}
          {...props}
        />
      </div>
      {showAddButton && (
        <>
          <button
            type="button"
            className={`h-10 w-10 flex items-center justify-center rounded-full focus:outline-none ${
              canCreateData() 
                ? 'bg-green-500 text-white hover:bg-green-600' 
                : 'bg-gray-300 text-gray-500 cursor-not-allowed'
            }`}
            onClick={handleAddClick}
            title={canCreateData() 
              ? `Add new ${ENTITY_LABELS[entityType] || entityType}` 
              : `Vous n'avez pas les permissions pour créer un ${ENTITY_LABELS[entityType] || entityType}`
            }
          >
            <span className="text-2xl font-bold">+</span>
          </button>
          {/* Modal */}
          {isModalOpen && (
            <div 
              className="fixed inset-0 z-50 flex items-center justify-center modal-overlay-stable"
              style={{
                backgroundColor: 'rgba(0, 0, 0, 0.4)'
              }}
            >
              <div className="bg-white rounded-lg shadow-2xl w-full max-w-md mx-4 modal-stable no-shake">
                <div className="relative">
                  <button
                    className="absolute top-4 right-4 text-gray-400 hover:text-gray-600 transition-colors duration-200 z-10"
                    onClick={() => setIsModalOpen(false)}
                  >
                    <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                      <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                    </svg>
                  </button>
                  <div className="p-6">
                    {renderAddForm()}
                  </div>
                </div>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}

export default EntitySelectWithAdd; 